import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Save, Plus, Trash2, GripVertical, Eye, EyeOff } from "lucide-react";

/**
 * Block-based landing-page editor.
 * Supports: hero, text, image, cta, columns.
 * Persists to `cms_pages` collection via /api/admin/cms/pages/{slug}.
 */
const BLOCK_TYPES = [
  { key: "hero", label: "Hero" },
  { key: "text", label: "Text section" },
  { key: "image", label: "Image" },
  { key: "cta", label: "CTA button" },
  { key: "columns", label: "Columns" },
];

const DEFAULT_PAGES = [
  { slug: "foodpulse-home", title: "FoodPulse — Home" },
  { slug: "food-pulse-deals", title: "Deals landing" },
  { slug: "food-pulse-pantry", title: "Smart Pantry landing" },
  { slug: "food-pulse-recipes", title: "Recipe Discovery landing" },
];

export default function CmsPagesEditor() {
  const [pages, setPages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadList = async () => {
    try {
      const r = await http.get("/admin/cms/pages");
      const existing = r.data?.pages || [];
      // Merge in the well-known default slugs so admins can start editing
      // immediately without having to guess the naming convention.
      const bySlug = new Map(existing.map((p) => [p.slug, p]));
      const seeded = DEFAULT_PAGES.filter((p) => !bySlug.has(p.slug))
        .map((p) => ({ ...p, blocks: [], status: "draft", site_slug: "foodpulse" }));
      setPages([...existing, ...seeded]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load pages");
    }
  };
  useEffect(() => { loadList(); }, []);

  const openPage = async (slug) => {
    try {
      const r = await http.get(`/admin/cms/pages/${encodeURIComponent(slug)}`);
      setSelected(r.data);
    } catch (e) {
      if (e.response?.status === 404) {
        // Blank template — the admin can start with an empty block list.
        const seed = pages.find((p) => p.slug === slug);
        setSelected(seed || { slug, title: slug, blocks: [], status: "draft", site_slug: "foodpulse" });
      } else {
        toast.error(e.response?.data?.detail || "Failed to load page");
      }
    }
  };

  if (selected) return <PageEditor page={selected} onBack={() => { setSelected(null); loadList(); }} busy={busy} setBusy={setBusy} />;

  return (
    <div className="max-w-3xl" data-testid="cms-pages-list">
      <Link to="/admin/cms" className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to CMS home
      </Link>
      <h1 className="text-2xl font-semibold text-slate-900">Pages</h1>
      <p className="mt-1 text-sm text-slate-500">
        Inline-edit hero, tagline, marketing landing content across foodpulse.net. Every page is a stack of blocks — reorder or delete anytime.
      </p>
      <div className="mt-4 rounded-xl border border-slate-200 bg-white">
        {pages.map((p) => (
          <button
            key={p.slug}
            onClick={() => openPage(p.slug)}
            data-testid={`cms-page-open-${p.slug}`}
            className="flex w-full items-center justify-between border-b border-slate-100 px-4 py-3 text-left last:border-b-0 hover:bg-slate-50"
          >
            <div>
              <div className="text-sm font-medium text-slate-900">{p.title}</div>
              <div className="text-xs text-slate-500">/{p.slug} · {p.blocks?.length || 0} blocks</div>
            </div>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${p.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
              {p.status || "draft"}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function PageEditor({ page, onBack, busy, setBusy }) {
  const [doc, setDoc] = useState(page);
  const set = (k, v) => setDoc((d) => ({ ...d, [k]: v }));

  const addBlock = (type) => {
    setDoc((d) => ({
      ...d,
      blocks: [...(d.blocks || []), { type, data: defaultBlockData(type), order: d.blocks?.length || 0 }],
    }));
  };
  const updateBlock = (i, patch) => {
    setDoc((d) => ({
      ...d,
      blocks: d.blocks.map((b, j) => j === i ? { ...b, data: { ...b.data, ...patch } } : b),
    }));
  };
  const removeBlock = (i) => setDoc((d) => ({ ...d, blocks: d.blocks.filter((_, j) => j !== i) }));
  const moveBlock = (i, dir) => setDoc((d) => {
    const arr = [...d.blocks];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return d;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...d, blocks: arr };
  });

  const save = async (status) => {
    setBusy(true);
    try {
      const body = {
        slug: doc.slug,
        title: doc.title,
        site_slug: doc.site_slug || "foodpulse",
        status: status || doc.status || "draft",
        blocks: doc.blocks || [],
      };
      const r = await http.put(`/admin/cms/pages/${encodeURIComponent(doc.slug)}`, body);
      setDoc(r.data);
      toast.success(`Page ${status || doc.status || "draft"} saved`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl" data-testid="cms-page-editor">
      <button onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to all pages
      </button>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <Input value={doc.title} onChange={(e) => set("title", e.target.value)} className="text-2xl font-semibold" data-testid="cms-page-title" />
          <div className="mt-1 text-xs text-slate-500">/{doc.slug}</div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => save("draft")} disabled={busy} data-testid="cms-page-save-draft">
            <EyeOff className="mr-1 h-4 w-4" /> Save draft
          </Button>
          <Button onClick={() => save("published")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cms-page-publish">
            <Eye className="mr-1 h-4 w-4" /> Publish
          </Button>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {(doc.blocks || []).map((b, i) => (
          <BlockCard
            key={b.id || `${b.type}-${i}`}
            i={i}
            block={b}
            onChange={(patch) => updateBlock(i, patch)}
            onRemove={() => removeBlock(i)}
            onMoveUp={() => moveBlock(i, -1)}
            onMoveDown={() => moveBlock(i, 1)}
          />
        ))}
        {(doc.blocks || []).length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
            No blocks yet — add your first section below.
          </div>
        )}
      </div>

      <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3">
        <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-emerald-700">Add block</div>
        <div className="flex flex-wrap gap-2">
          {BLOCK_TYPES.map((bt) => (
            <button
              key={bt.key}
              onClick={() => addBlock(bt.key)}
              data-testid={`cms-add-block-${bt.key}`}
              className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-white px-3 py-1.5 text-xs font-medium text-emerald-800 hover:bg-emerald-100"
            >
              <Plus className="h-3 w-3" /> {bt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function BlockCard({ i, block, onChange, onRemove, onMoveUp, onMoveDown }) {
  const { type, data } = block;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`cms-block-${type}`}>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <GripVertical className="h-4 w-4" />
          <span className="uppercase font-semibold tracking-wider">{type}</span>
        </div>
        <div className="flex gap-1">
          <IconBtn onClick={onMoveUp} title="Move up">↑</IconBtn>
          <IconBtn onClick={onMoveDown} title="Move down">↓</IconBtn>
          <IconBtn onClick={onRemove} title="Delete" danger data-testid={`cms-block-remove-${i}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </IconBtn>
        </div>
      </div>
      {type === "hero" && (
        <div className="space-y-2">
          <Row label="Title"><Input value={data.title || ""} onChange={(e) => onChange({ title: e.target.value })} /></Row>
          <Row label="Subtitle"><Input value={data.subtitle || ""} onChange={(e) => onChange({ subtitle: e.target.value })} /></Row>
          <Row label="CTA label"><Input value={data.cta_label || ""} onChange={(e) => onChange({ cta_label: e.target.value })} /></Row>
          <Row label="CTA URL"><Input value={data.cta_url || ""} onChange={(e) => onChange({ cta_url: e.target.value })} /></Row>
          <Row label="Background image URL"><Input value={data.image_url || ""} onChange={(e) => onChange({ image_url: e.target.value })} /></Row>
        </div>
      )}
      {type === "text" && (
        <div className="space-y-2">
          <Row label="Heading"><Input value={data.heading || ""} onChange={(e) => onChange({ heading: e.target.value })} /></Row>
          <Row label="Body (HTML allowed)"><Textarea rows={6} value={data.body_html || ""} onChange={(e) => onChange({ body_html: e.target.value })} /></Row>
        </div>
      )}
      {type === "image" && (
        <div className="space-y-2">
          <Row label="Image URL"><Input value={data.url || ""} onChange={(e) => onChange({ url: e.target.value })} /></Row>
          <Row label="Alt text"><Input value={data.alt || ""} onChange={(e) => onChange({ alt: e.target.value })} /></Row>
          <Row label="Caption"><Input value={data.caption || ""} onChange={(e) => onChange({ caption: e.target.value })} /></Row>
        </div>
      )}
      {type === "cta" && (
        <div className="space-y-2">
          <Row label="Label"><Input value={data.label || ""} onChange={(e) => onChange({ label: e.target.value })} /></Row>
          <Row label="URL"><Input value={data.url || ""} onChange={(e) => onChange({ url: e.target.value })} /></Row>
        </div>
      )}
      {type === "columns" && (
        <ColumnsEditor items={data.items || []} onChange={(items) => onChange({ items })} />
      )}
    </div>
  );
}

function ColumnsEditor({ items, onChange }) {
  const add = () => onChange([...items, { heading: "", body: "" }]);
  const update = (i, patch) => onChange(items.map((it, j) => j === i ? { ...it, ...patch } : it));
  const remove = (i) => onChange(items.filter((_, j) => j !== i));
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="rounded-md border border-slate-100 bg-slate-50 p-2">
          <Row label={`Column ${i + 1} heading`}><Input value={it.heading || ""} onChange={(e) => update(i, { heading: e.target.value })} /></Row>
          <Row label="Body"><Textarea rows={3} value={it.body || ""} onChange={(e) => update(i, { body: e.target.value })} /></Row>
          <button onClick={() => remove(i)} className="text-xs text-rose-600 hover:underline">Remove column</button>
        </div>
      ))}
      <button onClick={add} className="rounded-md border border-emerald-200 bg-white px-3 py-1 text-xs text-emerald-700 hover:bg-emerald-50">+ Add column</button>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-1 gap-1 sm:grid-cols-[140px_1fr]">
      <Label className="pt-1.5 text-xs text-slate-600">{label}</Label>
      {children}
    </div>
  );
}

function IconBtn({ children, onClick, title, danger, ...rest }) {
  return (
    <button
      onClick={onClick}
      title={title}
      {...rest}
      className={`grid h-7 w-7 place-content-center rounded-md border text-xs ${
        danger ? "border-rose-200 text-rose-600 hover:bg-rose-50" : "border-slate-200 text-slate-600 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}

function defaultBlockData(type) {
  switch (type) {
    case "hero":    return { title: "Welcome to FoodPulse", subtitle: "", cta_label: "Explore deals", cta_url: "/food-pulse/deals", image_url: "" };
    case "text":    return { heading: "", body_html: "" };
    case "image":   return { url: "", alt: "", caption: "" };
    case "cta":     return { label: "Learn more", url: "" };
    case "columns": return { items: [{ heading: "", body: "" }, { heading: "", body: "" }] };
    default:        return {};
  }
}
